(() => {
  if (customElements.get('source-reviews')) return;
  const requests = new Map();
  const getData = (url) => {
    if (!requests.has(url)) requests.set(url, fetch(url, { credentials: 'omit' }).then((response) => {
      if (!response.ok) throw new Error('Reviews unavailable');
      return response.json();
    }));
    return requests.get(url);
  };
  class SourceReviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.list = this.querySelector('[data-reviews-list]');
      this.status = this.querySelector('[data-reviews-status]');
      this.mode = this.dataset.mode;
      this.pageSize = 5;
      this.page = 1;
      this.paused = false;
      this.visible = false;
      this.querySelector('[data-reviews-view-all]')?.addEventListener('click', () => this.querySelector('.source-reviews__viewport').scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
      this.querySelector('[data-reviews-previous]')?.addEventListener('click', () => this.slide(-1));
      this.querySelector('[data-reviews-next]')?.addEventListener('click', () => this.slide(1));
      this.querySelector('[data-reviews-pause]')?.addEventListener('click', (event) => {
        this.paused = !this.paused;
        event.currentTarget.textContent = this.paused ? 'Reprendre le défilement' : 'Mettre le défilement en pause';
        this.updateTimer();
      });
      this.addEventListener('mouseenter', () => { this.hovered = true; this.updateTimer(); });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.updateTimer(); });
      this.addEventListener('focusin', () => { this.focused = true; this.updateTimer(); });
      this.addEventListener('focusout', () => { this.focused = false; this.updateTimer(); });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          this.updateTimer();
        });
        this.observer.observe(this);
      } else this.visible = true;
      getData(this.dataset.source).then((data) => {
        if (!this.isConnected) return;
        this.data = data;
        this.reviews = Array.isArray(data.reviews) ? data.reviews : [];
        if (!this.reviews.length) { this.hidden = true; return; }
        const average = Number(data.average);
        const total = Number(data.count);
        this.querySelector('[data-reviews-average]').textContent = `${average.toFixed(1)}${this.mode === 'product' ? '' : '/5'}`;
        const headerStars = this.querySelector('.source-reviews__stars');
        headerStars.style.color = 'transparent';
        headerStars.style.backgroundImage = `linear-gradient(90deg,#e7721b ${average * 20}%,#d7dae4 ${average * 20}%)`;
        headerStars.style.backgroundClip = 'text';
        this.querySelector('[data-reviews-count]').textContent = this.mode === 'product' ? `Basé sur ${total.toLocaleString('fr-FR')} avis` : `(${total.toLocaleString('fr-FR')} avis)`;
        this.querySelector('[data-reviews-summary]').hidden = false;
        if (this.mode === 'product') this.distribution(data.star_distribution);
        else {
          this.reviews = this.reviews.slice(0, Number(this.dataset.carouselLimit) || 12);
          this.querySelector('[data-reviews-arrows]').hidden = false;
          const pause = this.querySelector('[data-reviews-pause]');
          if (pause) pause.hidden = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }
        this.filter();
        this.updateTimer();
      }).catch(() => { this.status.textContent = 'Les avis ne sont pas disponibles pour le moment.'; });
    }
    disconnectedCallback() { clearInterval(this.timer); this.observer?.disconnect(); }
    filter() {
      this.filtered = this.reviews;
      this.page = 1;
      this.render();
    }
    distribution(source) {
      const target = this.querySelector('[data-reviews-distribution]');
      const counts = source || Object.fromEntries([1, 2, 3, 4, 5].map((score) => [score, this.reviews.filter((r) => Number(r.score) === score).length]));
      const maximum = Math.max(Object.values(counts).reduce((sum, value) => sum + Number(value), 0), 1);
      target.replaceChildren(...[5, 4, 3, 2, 1].map((score) => {
        const row = document.createElement('div'); row.className = 'review-distribution__row';
        row.setAttribute('aria-label', `${score} étoiles : ${counts[score] || 0} avis`);
        const index = document.createElement('span'); index.textContent = `${score} ★`;
        const bar = document.createElement('span'); bar.className = 'review-distribution__bar';
        const fill = document.createElement('span'); fill.style.width = `${Number(counts[score] || 0) / maximum * 100}%`;bar.append(fill);
        const count = document.createElement('span'); count.textContent = String(counts[score] || 0);
        row.append(index, bar, count); return row;
      }));
    }
    render() {
      const shown = this.mode === 'carousel' ? this.filtered : this.filtered.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
      this.list.replaceChildren(...shown.map((review) => this.card(review)));
      if (this.mode === 'product') this.pagination();
      this.status.textContent = '';
    }
    pagination() {
      const nav = this.querySelector('[data-reviews-pagination]');
      const total = Math.ceil(this.reviews.length / this.pageSize);
      nav.hidden = total <= 1;
      let first = this.page - 2, last = this.page + 2;
      if (last > total) { first -= last - total; last = total; }
      if (first <= 0) { last += 1 - first; first = 1; }
      last = Math.min(last, total);
      const make = (page, label, aria) => {
        const button = document.createElement('button'); button.type = 'button'; button.textContent = label;
        button.setAttribute('aria-label', aria); button.disabled = page < 1 || page > total;
        if (page === this.page && label !== '‹' && label !== '›') button.setAttribute('aria-current', 'page');
        button.addEventListener('click', () => { this.page = page; this.render(); this.querySelector('.source-reviews__viewport').scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }); });
        return button;
      };
      const buttons = [make(this.page - 1, '‹', 'Page précédente')];
      for (let page = first; page <= last; page++) buttons.push(make(page, String(page), `Page ${page}`));
      buttons.push(make(this.page + 1, '›', 'Page suivante'));
      nav.replaceChildren(...buttons);
    }
    card(review) {
      const card = this.querySelector('[data-review-template]').content.firstElementChild.cloneNode(true);
      card.dataset.reviewId = String(review.id);
      const productHandle = this.mode === 'product' ? this.dataset.productHandle : review.product_handle;
      const url = productHandle ? `${this.dataset.productRoot}${encodeURIComponent(productHandle)}#product-reviews` : this.dataset.storeUrl;
      card.querySelector('[data-review-author]').textContent = review.author || 'Nom non renseigné';
      const verified = card.querySelector('[data-review-verified]');
      verified.hidden = !review.verified_buyer && review.reviewer_type !== 'User';
      card.querySelector('[data-review-verified-label]').textContent = review.verified_buyer ? 'Acheteur vérifié' : 'Client vérifié';
      const country = card.querySelector('[data-review-country]');
      if (country && review.country?.label) { country.hidden = false; country.textContent = review.country.label; }
      const time = card.querySelector('[data-review-date]');
      const date = new Date(review.created_at);
      if (Number.isFinite(date.getTime())) {
        time.dateTime = review.created_at;
        time.textContent = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: 'Europe/Paris' });
      }
      const stars = card.querySelector('[data-review-stars]');
      const score = Math.max(0, Math.min(5, Math.round(Number(review.score))));
      stars.textContent = '★'.repeat(score) + '☆'.repeat(5 - score);
      stars.setAttribute('aria-label', `${score} sur 5`);
      const title = card.querySelector('[data-review-title-link]');
      title.textContent = review.title;
      title.href = url;
      const content = card.querySelector('[data-review-content]');
      content.id = `ReviewBody-${this.id}-${review.id}`;
      content.textContent = review.content;
      const expand = card.querySelector('[data-review-expand]');
      if (this.mode === 'carousel' && review.content.length > 200) {
        expand.hidden = false;
        expand.setAttribute('aria-controls', content.id);
        expand.addEventListener('click', () => {
          const expanded = card.classList.toggle('review-card--expanded');
          expand.setAttribute('aria-expanded', String(expanded));
          expand.textContent = expanded ? 'Réduire l’avis' : 'Lire l’avis complet';
        });
      }
      const incentive = card.querySelector('[data-review-incentive]');
      if (review.is_incentivized && this.dataset.incentiveBadges === 'true') {
        incentive.hidden = false;
        incentive.textContent = review.incentive_type === 'yotpo_loyalty_points' ? 'Avis récompensé par des points de fidélité' : 'Avis récompensé';
      }
      const productLink = card.querySelector('[data-review-product-link]');
      productLink.href = url;
      card.querySelector('[data-review-product-title]').textContent = review.product_title || 'Avis sur la boutique';
      const image = card.querySelector('[data-review-image]');
      if (review.product_asset) {
        image.src = new URL(review.product_asset, new URL(this.dataset.assetBase, document.baseURI)).href;
        image.alt = review.product_title || '';
      } else image.remove();
      const comment = card.querySelector('[data-review-comment]');
      if (comment && review.comment?.content) { comment.hidden = false; comment.querySelector('[data-review-comment-content]').textContent = review.comment.content; }
      const photos = card.querySelector('[data-review-photos]');
      if (photos && review.images?.some((photo) => photo.asset)) {
        photos.hidden = false;
        review.images.filter((photo) => photo.asset).forEach((photo, index) => {
          const button = document.createElement('button'); button.type = 'button';button.setAttribute('aria-label', `Photo ${index + 1} de l’avis de ${review.author || 'client'}`);
          const thumbnail = document.createElement('img');thumbnail.src = new URL(photo.asset, new URL(this.dataset.assetBase, document.baseURI)).href;thumbnail.alt = `Photo de l’avis ${review.title}`;thumbnail.loading = 'lazy';thumbnail.width = 80;thumbnail.height = 80;button.append(thumbnail);
          button.addEventListener('click', () => { const dialog = this.querySelector('[data-review-photo-dialog]'); const large = dialog.querySelector('[data-review-photo-large]');large.src = thumbnail.src;large.alt = thumbnail.alt;if (window.openDialog) window.openDialog(dialog.id, button);else dialog.showModal(); });
          photos.append(button);
        });
      }
      return card;
    }
    slide(direction) {
      const step = (this.list.firstElementChild?.getBoundingClientRect().width || 330) + 24;
      const maximum = this.list.scrollWidth - this.list.clientWidth;
      if (direction > 0 && this.list.scrollLeft >= maximum - 4) this.list.scrollTo({ left: 0, behavior: 'smooth' });
      else this.list.scrollBy({ left: direction * step, behavior: 'smooth' });
    }
    updateTimer() {
      clearInterval(this.timer);
      if (this.mode !== 'carousel' || this.dataset.autoplay !== 'true' || !this.data || !this.visible || this.paused || this.hovered || this.focused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = setInterval(() => this.slide(1), 4500);
    }
  }
  customElements.define('source-reviews', SourceReviews);
})();
